import { MRT_ColumnDef } from "material-react-table"
import { useMemo } from "react"
import { ReporteVentaItem } from "../interfaces/erp/reporte-venta/reporte-venta.interface"
import { cellNotFilter } from "../utils"
import { Chip, Link, Tooltip } from "@mui/material"
import { format } from "date-fns"

interface UseTableColumnsReporteVentaProps {
  pagination?: { pageIndex: number; pageSize: number };
}

export const useTableColumnsReporteVenta = (props?: UseTableColumnsReporteVentaProps) => {
  const { pagination } = props || {};

  const columnBase = useMemo<MRT_ColumnDef<ReporteVentaItem>[]>(() => [
    {
      accessorKey: 'numero',
      header: '#',
      size: 60,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        // Calcular número absoluto basado en la página actual
        const baseNumber = pagination ? (pagination.pageIndex * pagination.pageSize) : 0;
        return baseNumber + row.index + 1;
      },
    },
    {
      accessorKey: 'tipo',
      header: 'Tipo',
      size: 120,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'vendedor',
      header: 'Vendedor',
      size: 200,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'cargo',
      header: 'Área Ventas',
      size: 150,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      ...cellNotFilter,
      accessorKey: 'fecha',
      header: 'Fecha Instalación',
      size: 150,
      Cell: ({ cell }) => {
        const fecha = cell.getValue() as string;
        if (fecha) {
          try {
            return format(new Date(fecha), 'dd/MM/yyyy');
          } catch {
            return fecha;
          }
        }
        return '-';
      },
    },
    {
      accessorKey: 'nombre_cliente',
      header: 'Cliente',
      size: 300,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'ciudad',
      header: 'Ciudad',
      size: 150,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'sector',
      header: 'Sector',
      size: 150,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      ...cellNotFilter,
      accessorKey: 'coordenadas',
      header: 'Coordenadas',
      size: 180,
      Cell: ({ cell }) => {
        const coordenadas = cell.getValue() as string;
        if (!coordenadas || coordenadas === '-') return '-';
        
        const [lat, lng] = coordenadas.split(',');
        
        if (!lat || !lng) return coordenadas;
        
        const mapsUrl = `https://www.google.com/maps?q=${lat.trim()},${lng.trim()}`;
        
        return (
          <Tooltip title="Click para ver en Google Maps">
            <Link 
              href={mapsUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ 
                fontSize: '0.875rem',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              📍 {lat.trim().substring(0, 8)}, {lng.trim().substring(0, 8)}
            </Link>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'tecnico',
      header: 'Técnico',
      size: 150,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'forma_pago',
      header: 'Forma Pago',
      size: 130,
      Cell: ({ cell }) => cell.getValue<string>() || '-',
    },
    {
      accessorKey: 'Banco',
      header: 'Banco',
      size: 150,
      Cell: ({ cell }) => {
        const banco = cell.getValue<string>();
        return banco || '-';
      },
    },
    {
      accessorKey: 'estado_venta',
      header: 'Estado Venta',
      size: 150,
      Cell: ({ cell }) => {
        const estado = cell.getValue() as string;
        const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
          'Exitosa': 'success',
          'Pendiente': 'warning',
          'Fallido': 'error',
          'En Proceso': 'info',
        };
        return (
          <Chip
            label={estado || '-'}
            color={colorMap[estado] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      accessorKey: 'estado_instalacion',
      header: 'Estado Instalación',
      size: 160,
      Cell: ({ cell }) => {
        const estado = cell.getValue() as string;
        const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
          'completado': 'success',
          'ok': 'success',
          'en proceso': 'warning',
          'fallido': 'error',
          'pendiente': 'info',
        };
        return estado ? (
          <Chip
            label={estado}
            color={colorMap[estado.toLowerCase()] || 'default'}
            size="small"
          />
        ) : '-';
      },
    },
    {
      accessorKey: 'nombre_plan_factura',
      header: 'Plan Comercial',
      size: 150,
      Cell: ({ cell }) => {
        const planComercial = cell.getValue<string>();
        return planComercial || '-';
      },
    },
    {
      accessorKey: 'nombre_plan',
      header: 'Plan Técnico',
      size: 150,
      Cell: ({ cell }) => {
        const plan = cell.getValue() as string;
        return plan ? (
          <Chip
            label={plan}
            size="small"
            variant="outlined"
            color="primary"
          />
        ) : '-';
      },
    },
    {
      accessorKey: 'tipo_plan',
      header: 'Tipo Plan',
      size: 130,
      Cell: ({ cell }) => {
        const tipoPlan = cell.getValue() as string;
        const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
          'Home': 'info',
          'Corporativo': 'success',
          'Pymes': 'warning',
          'ISP': 'error',
        };
        return tipoPlan ? (
          <Chip
            label={tipoPlan}
            color={colorMap[tipoPlan] || 'default'}
            size="small"
          />
        ) : '-';
      },
    },
    {
      accessorKey: 'valor_mensual',
      header: 'Costo Mensual',
      size: 130,
      Cell: ({ cell }) => {
        const valor = cell.getValue();
        return valor ? `$${Number(valor).toFixed(2)}` : '-';
      },
    },
    {
      accessorKey: 'valor_instalacion',
      header: 'Costo Instalación',
      size: 150,
      Cell: ({ cell }) => {
        const valor = cell.getValue();
        return valor ? `$${Number(valor).toFixed(2)}` : '-';
      },
    },
    {
      accessorKey: 'tipo_servicio',
      header: 'Servicio Actual',
      size: 150,
      Cell: ({ cell }) => {
        const servicio = cell.getValue<string>();
        return servicio || '-';
      },
    },
    {
      accessorKey: 'tipo_instalacion',
      header: 'Obs. Adicional',
      size: 200,
      Cell: ({ cell }) => {
        const observacion = cell.getValue<string>();
        return observacion || '-';
      },
    },
    {
      accessorKey: 'equipo',
      header: 'Equipo',
      size: 150,
      Cell: ({ cell }) => {
        const equipo = cell.getValue<string>();
        return equipo || '-';
      },
    },
    {
      accessorKey: 'buro',
      header: 'Buró',
      size: 120,
      Cell: ({ cell }) => {
        const buro = cell.getValue();
        if (!buro) return '-';
        
        const valor = Number(buro);
        let color: 'success' | 'warning' | 'error' = 'success';
        
        if (valor >= 700) color = 'success';
        else if (valor >= 500) color = 'warning';
        else color = 'error';
        
        return (
          <Chip 
            label={valor.toFixed(0)} 
            color={color}
            size="small"
          />
        );
      },
    },
    {
      accessorKey: 'estado_contrato',
      header: 'Estado Contrato',
      size: 150,
      Cell: ({ cell }) => {
        const estadoContrato = cell.getValue<string>();
        return estadoContrato || '-';
      },
    },
    {
      accessorKey: 'licenciamiento',
      header: 'Licenciamiento',
      size: 150,
      Cell: ({ cell }) => {
        const licencia = cell.getValue() as string;
        return licencia ? (
          <Chip 
            label={licencia} 
            color={licencia.toUpperCase() === 'SI' ? 'success' : 'default'}
            size="small"
          />
        ) : '-';
      },
    },
    {
      accessorKey: 'medio',
      header: 'Medio Publicitario',
      size: 170,
      Cell: ({ cell }) => {
        const medio = cell.getValue<string>();
        return medio || '-';
      },
    },
    {
      accessorKey: 'herramienta',
      header: 'Herramienta Utilizada',
      size: 180,
      Cell: ({ cell }) => {
        const herramienta = cell.getValue<string>();
        return herramienta || '-';
      },
    },
    {
      accessorKey: 'router_adicional',
      header: 'Existe Router AC',
      size: 150,
      Cell: ({ cell }) => {
        const router = cell.getValue() as string;
        if (!router || router === '-') return '-';
        
        return (
          <Chip 
            label={router} 
            color="info"
            size="small"
          />
        );
      },
    },
    {
      accessorKey: 'detalle_utp',
      header: 'UTP Utilizado',
      size: 130,
      Cell: ({ cell }) => {
        const utp = cell.getValue<string>();
        return utp || '-';
      },
    },
    {
      accessorKey: 'detalle_promocion',
      header: 'Promoción Aplicada',
      size: 170,
      Cell: ({ cell }) => {
        const promocion = cell.getValue<string>();
        return promocion || '-';
      },
    },
    {
      accessorKey: 'referido',
      header: 'Promoción Referido',
      size: 170,
      Cell: ({ cell }) => {
        const referido = cell.getValue<string>();
        return referido || '-';
      },
    },
    {
      accessorKey: 'motivo_fallido',
      header: 'Motivo Fallido',
      size: 200,
      Cell: ({ cell }) => {
        const motivo = cell.getValue() as string;
        if (!motivo) return '-';

        const textoCorto = motivo.length > 100 ? `${motivo.substring(0, 100)}...` : motivo;

        return (
          <Tooltip title={motivo}>
            <span style={{ cursor: 'help' }}>{textoCorto}</span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'idContrato',
      header: 'ID CONTRATO',
      size: 130,
      Cell: ({ cell }) => {
        const idContrato = cell.getValue<string>();
        return idContrato ? (
          <Chip
            label={idContrato}
            color="primary"
            size="small"
            variant="outlined"
          />
        ) : '-';
      },
    },
  ], [pagination])

  return {
    columnBase
  }
}
